import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { getAnypointStudioP2RepositoryUrl } from '~/constants/pluginDownloadUrls'
import { bridge } from '~/services/bridge'
import { BetterToast } from '~/utils/toast'
import styles from './settings.module.scss'
import { cm } from '~/utils/utils'

const STEPS = [
  {
    title: 'Open Anypoint Studio',
    description: null
  },
  {
    title: 'Go to Help > Install New Software..',
    description: null
  },
  {
    title: 'Enter the following URL:',
    description: null,
    hasSnippet: true
  },
  {
    title: 'Click Add and follow the installation wizard',
    description: null
  }
]

function CopySnippet({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      BetterToast.success('Copied to clipboard')
    } catch {
      BetterToast.error('Failed to copy')
    }
  }

  return (
    <button
      type='button'
      onClick={handleCopy}
      className='flex items-center gap-2 bg-tokens-neutral-bg-grey-2 rounded-xl px-3 py-4 w-full cursor-pointer hover:bg-[rgba(0,0,0,0.06)] transition-colors text-left'
      aria-label='Copy URL to clipboard'
    >
      {copied ? (
        <Check className='w-4 h-4 shrink-0 text-tokens-status-success-1' />
      ) : (
        <Copy className='w-4 h-4 shrink-0 text-tokens-neutral-icon-solid' />
      )}
      <span className='font-mono font-medium text-[12px] leading-normal text-[rgba(0,0,0,0.45)] break-all'>
        {text}
      </span>
    </button>
  )
}

export function DownloadInstructionsSettingsTab(): JSX.Element {
  const studioP2RepoUrl = getAnypointStudioP2RepositoryUrl(bridge.getBaseUrl())

  return (
    <>
      <div className={cm('w-full h-full overflow-auto pb-32', styles['no-scroll-bar'])}>
        <div className='flex flex-col gap-6'>
          <div className='bg-tokens-neutral-fill-fill-1 border border-tokens-neutral-border-2 rounded-3xl px-12 py-6 flex flex-col gap-10'>
            {/* Title */}
            <div className='flex justify-center'>
              <span className='font-sans font-bold text-[14px] leading-[18px] text-primary select-none'>
                Install CurieTech AI Plugin
              </span>
            </div>

            {/* Steps */}
            <div className='flex flex-col gap-10'>
              {STEPS.map((step, index) => (
                <div key={step.title} className='flex gap-5 items-start'>
                  {/* Step number - Avatar Display variant */}
                  <div className='flex items-center justify-center w-6 h-6 rounded-full bg-tokens-status-link-base shrink-0 select-none'>
                    <span className='font-sans font-normal text-[12px] leading-[20px] text-primary'>
                      {index + 1}
                    </span>
                  </div>

                  {/* Step content */}
                  <div className='flex flex-col gap-2 flex-1 min-w-0'>
                    <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] select-none'>
                      {step.title}
                    </span>
                    {step.description && (
                      <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)] select-none'>
                        {step.description}
                      </span>
                    )}
                    {step.hasSnippet && <CopySnippet text={studioP2RepoUrl} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DownloadInstructionsSettingsTab
